import React, { useState, useEffect, useRef } from 'react';
import { Spin, Avatar } from 'antd';
import { Building2, Save, Upload as UploadIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import Inputs from '../../components/inputs';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { BUCKET_URL } from '../../config/config';
import { useFileUpload } from '../../hooks/useFileUpload';
import {
  getOrganization,
  updateOrganization,
  getPostalData,
  OrganizationData,
  PostalData,
  PostalApiResponse
} from '../../services/organizationService';
import '../../styles/designSystem.css';
import './settings.scss';
const OrganizationSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [fetchingPostal, setFetchingPostal] = useState(false);
  const [cityOptions, setCityOptions] = useState<PostalData[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [orgData, setOrgData] = useState<OrganizationData>({
    name: '',
    phone: '',
    countryCode: '+91',
    currency: 'USD',
    timeZone: '',
    website: '',
    address: '',
    description: '',
    socialLinks: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    },
    iconUrl: '',
    profileUrl: '',
    faviconUrl: '',
    isAutomationAutoTaskAssignEnable: false,
    isIntegrationDuplicateLeadAllow: false,
    supportContact: {
      phone: '',
      whatsapp: '',
      email: ''
    },
    taskNotificationOverdueDays: 0,
    lastLeadSequenceId: '',
    isCallRecordingEnabled: false,
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    gst: '',
    businessCategory: ''
  });

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    setLoading(true);
    try {
      const response = await getOrganization();
      if (response && response.data && response.data.length > 0) {
        const org = response.data[0];
        setOrganizationId(org._id);
        setOrgData({
          name: org.name || '',
          phone: org.phone || '',
          countryCode: org.countryCode || '91',
          currency: org.currency || 'USD',
          timeZone: org.timeZone || '',
          website: org.website || '',
          address: org.address || '',
          description: org.description || '',
          socialLinks: org.socialLinks || {
            linkedin: '',
            twitter: '',
            facebook: '',
            instagram: ''
          },
          iconUrl: org.iconUrl || '',
          profileUrl: org.profileUrl || '',
          faviconUrl: org.faviconUrl || '',
          isAutomationAutoTaskAssignEnable: org.isAutomationAutoTaskAssignEnable || false,
          isIntegrationDuplicateLeadAllow: org.isIntegrationDupCheckEnabled || false,
          supportContact: org.supportContact || {
            phone: '',
            whatsapp: '',
            email: ''
          },
          taskNotificationOverdueDays: org.taskNotificationOverdueDays || 0,
          lastLeadSequenceId: org.lastLeadSequenceId || '',
          isCallRecordingEnabled: org.isCallRecordingEnabled || false,
          address1: org.address1 || '',
          address2: org.address2 || '',
          city: org.city || '',
          state: org.state || '',
          country: org.country || '',
          zipCode: org.zipCode || '',
          gst: org.gst || '',
          businessCategory: org.businessCategory || ''
        });
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostalData = async (pincode: string) => {
    // Only fetch if pincode is 6 digits
    if (!/^\d{6}$/.test(pincode)) {
      return;
    }

    setFetchingPostal(true);
    try {
      const response = await getPostalData(pincode);

      if (response && response.length > 0 && response[0].Status === 'Success') {
        const postOffices = response[0].PostOffice;

        if (postOffices && postOffices.length > 0) {
          // Store all city options
          setCityOptions(postOffices);

          // Auto-populate with first result
          const firstResult = postOffices[0];
          setOrgData(prev => ({
            ...prev,
            country: firstResult.Country || '',
            state: firstResult.State || '',
            city: firstResult.District || firstResult.Name || ''
          }));

          if (postOffices.length > 1) {
            toast(`Found ${postOffices.length} locations. Please select the correct city.`, { icon: 'ℹ️' });
          } else {
            toast.success('Location details populated successfully');
          }
        }
      } else {
        toast.error('No location found for this PIN code');
        setCityOptions([]);
      }
    } catch (error) {
      console.error('Error fetching postal data:', error);
      toast.error('Failed to fetch location details');
      setCityOptions([]);
    } finally {
      setFetchingPostal(false);
    }
  };

  const handlePhoneChange = (value: string, country: any) => {
    const newCountryCode = country.dialCode;
    const numberWithoutCode = value.slice(newCountryCode.length);

    setOrgData(prev => ({
      ...prev,
      phone: numberWithoutCode,
      countryCode: newCountryCode
    }));
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setOrgData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof OrganizationData] as any),
          [child]: value
        }
      }));
    } else {
      setOrgData(prev => ({
        ...prev,
        [name]: value
      }));

      // Fetch postal data when zipCode changes
      if (name === 'zipCode' && value.length === 6) {
        fetchPostalData(value);
      }
    }
  };

  // File upload hook
  const { uploadFile, uploading: uploadingIcon, progress } = useFileUpload({
    recordId: organizationId,
    type: 'organization_icon_url',
    maxSize: 5,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    onSuccess: (files) => {
      if (files && files.length > 0) {
        setOrgData(prev => ({
          ...prev,
          iconUrl: files[0].filePath
        }));
        toast.success('Organization icon uploaded successfully');
      }
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error}`);
    }
  });

  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!organizationId) {
      toast.error('Organization ID not found. Please refresh the page.');
      return;
    }

    await uploadFile(file);
  };

  const handleSave = async () => {
    if (!organizationId) {
      toast.error('Organization ID not found. Please refresh the page.');
      return;
    }

    setSaving(true);
    try {
      // Prepare data for API - combine countryCode and phone, exclude countryCode from payload
      const { countryCode, ...restData } = orgData;
      const dataToSave = {
        ...restData,
        phone: countryCode && orgData.phone ? `${countryCode}${orgData.phone}` : orgData.phone
      };

      await updateOrganization(organizationId, dataToSave);
      toast.success('Organization settings saved successfully');
    } catch (error) {
      console.error('Error saving organization data:', error);
      toast.error('Failed to save organization settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-content-wrapper">
        <div className="ds-flex ds-items-center ds-justify-center" style={{ minHeight: '20rem' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-content-wrapper">
      <div className="ds-card" style={{ maxWidth: '60rem', margin: '0 auto' }}>
        {/* Icon Upload Section */}
        <div className="ds-form-section">
          <div className="section-header">
            <h3 className="ds-h4">Organization Profile</h3>
            <p className="helper-text">Upload your organization's logo. This will appear in invoices, emails, and across the platform.</p>
          </div>
          <div className="ds-flex ds-flex-col ds-items-center ds-gap-4" style={{ padding: '1rem 0' }}>
            <Avatar
              size={100}
              src={orgData.iconUrl ? `${BUCKET_URL}${orgData.iconUrl}` : undefined}
              icon={<Building2 />}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleIconUpload}
              style={{ display: 'none' }}
            />
            <button
              className="ds-btn ds-btn-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingIcon}
            >
              <UploadIcon size={16} />
              {uploadingIcon ? `Uploading... ${progress}%` : 'Upload Icon'}
            </button>
            <p className="ds-body-sm ds-text-tertiary" style={{ textAlign: 'center', margin: 0 }}>
              Recommended: 512x512px, Max 5MB (JPG, PNG, WEBP)
            </p>
          </div>
        </div>

        {/* Basic Information */}
        <div className="ds-form-section">
          <div className="section-header">
            <h3 className="ds-h4">Basic Information</h3>
            <p className="helper-text">Essential details about your organization including name, contact information, and business identifiers.</p>
          </div>
          <div className="ds-form-grid">
            <Inputs
              label="Organization Name"
              name="name"
              type="text"
              value={orgData.name}
              onChange={handleInputChange}
              placeholder="Enter organization name"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="org-name"
            />
            <div>
              <label className="ds-label">Phone</label>
              <PhoneInput
                country="in"
                value={`${orgData.countryCode}${orgData.phone}`}
                onChange={handlePhoneChange}
                containerClass="react-tel-input"
              />
            </div>
            <Inputs
              label="Currency"
              name="currency"
              type="text"
              value={orgData.currency}
              onChange={handleInputChange}
              placeholder="Currency"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              disabled={true}
              options={[]}
              id="org-currency"
            />
            <Inputs
              label="Time Zone"
              name="timeZone"
              type="text"
              value={orgData.timeZone}
              onChange={handleInputChange}
              placeholder="Time zone"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              disabled={true}
              options={[]}
              id="org-timezone"
            />
            <Inputs
              label="Website"
              name="website"
              type="url"
              value={orgData.website}
              onChange={handleInputChange}
              placeholder="https://example.com"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-website"
            />
            <Inputs
              label="GST Number"
              name="gst"
              type="text"
              value={orgData.gst}
              onChange={handleInputChange}
              placeholder="Enter GST number"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-gst"
            />
          </div>
        </div>

        {/* Address Information */}
        <div className="ds-form-section">
          <div className="section-header">
            <h3 className="ds-h4">Address Information</h3>
            <p className="helper-text">Your business address for official correspondence, invoices, and legal documentation.</p>
          </div>
          <div className="ds-form-grid">
            <div style={{ position: 'relative' }}>
              <Inputs
                label="PIN Code / Zip Code"
                name="zipCode"
                type="text"
                value={orgData.zipCode}
                onChange={handleInputChange}
                placeholder="Enter PIN code"
                className="form-control"
                autoComplete="off"
                isRequired={false}
                options={[]}
                id="org-zipcode"
              />
              {fetchingPostal && (
                <div style={{
                  position: 'absolute',
                  right: '10px',
                  top: '38px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Spin size="small" />
                </div>
              )}
            </div>
            <Inputs
              label="Country"
              name="country"
              type="text"
              value={orgData.country}
              onChange={handleInputChange}
              placeholder="Enter country"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-country"
            />
            <Inputs
              label="State"
              name="state"
              type={cityOptions.length > 0 ? 'select' : 'text'}
              value={orgData.state}
              onChange={handleInputChange}
              placeholder="Enter state"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={cityOptions.length > 0 ?
                Array.from(new Set(cityOptions.map(opt => opt.State)))
                  .map(state => ({ value: state, label: state }))
                : []
              }
              id="org-state"
              disabled={cityOptions.length > 0}
            />
            <Inputs
              label="City / District"
              name="city"
              type={cityOptions.length > 1 ? 'select' : 'text'}
              value={orgData.city}
              onChange={handleInputChange}
              placeholder="Enter city"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={cityOptions.length > 1 ?
                cityOptions.map(opt => ({
                  value: opt.District || opt.Name,
                  label: `${opt.Name} (${opt.District})`
                }))
                : []
              }
              id="org-city"
            />
            <Inputs
              label="Address Line 1"
              name="address1"
              type="text"
              value={orgData.address1}
              onChange={handleInputChange}
              placeholder="Enter address line 1"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-address1"
            />
            <Inputs
              label="Address Line 2"
              name="address2"
              type="text"
              value={orgData.address2}
              onChange={handleInputChange}
              placeholder="Enter address line 2"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-address2"
            />
          </div>
        </div>

        {/* Social Links */}
        <div className="ds-form-section">
          <div className="section-header">
            <h3 className="ds-h4">Social Links</h3>
            <p className="helper-text">Connect your social media profiles to enhance your organization's online presence and credibility.</p>
          </div>
          <div className="ds-form-grid">
            <Inputs
              label="LinkedIn"
              name="socialLinks.linkedin"
              type="url"
              value={orgData.socialLinks.linkedin}
              onChange={handleInputChange}
              placeholder="https://linkedin.com/company/..."
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-linkedin"
            />
            <Inputs
              label="Twitter"
              name="socialLinks.twitter"
              type="url"
              value={orgData.socialLinks.twitter}
              onChange={handleInputChange}
              placeholder="https://twitter.com/..."
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-twitter"
            />
            <Inputs
              label="Facebook"
              name="socialLinks.facebook"
              type="url"
              value={orgData.socialLinks.facebook}
              onChange={handleInputChange}
              placeholder="https://facebook.com/..."
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-facebook"
            />
            <Inputs
              label="Instagram"
              name="socialLinks.instagram"
              type="url"
              value={orgData.socialLinks.instagram}
              onChange={handleInputChange}
              placeholder="https://instagram.com/..."
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-instagram"
            />
          </div>
        </div>

        {/* Support Contact */}
        <div className="ds-form-section">
          <div className="section-header">
            <h3 className="ds-h4">Support Contact</h3>
            <p className="helper-text">Provide support contact information so customers can easily reach you for assistance.</p>
          </div>
          <div className="ds-form-grid">
            <Inputs
              label="Support WhatsApp"
              name="supportContact.whatsapp"
              type="phone"
              value={orgData.supportContact.whatsapp}
              onChange={handleInputChange}
              placeholder="Enter WhatsApp number"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="support-whatsapp"
            />
            <Inputs
              label="Support Email"
              name="supportContact.email"
              type="email"
              value={orgData.supportContact.email}
              onChange={handleInputChange}
              placeholder="support@example.com"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="support-email"
            />
          </div>
        </div>

        {/* Description */}
        <div className="ds-form-section">
          <div className="section-header">
            <h3 className="ds-h4">Description</h3>
            <p className="helper-text">A brief overview of your organization, its mission, and services. This may be visible to clients and partners.</p>
          </div>
          <Inputs
            label="Description"
            name="description"
            type="text"
            value={orgData.description}
            onChange={handleInputChange}
            placeholder="Enter organization description"
            className="form-control"
            autoComplete="off"
            isRequired={false}
            options={[]}
            id="org-description"
          />
        </div>

        {/* Save Button */}
        <div className="ds-card-footer">
          <button
            className="ds-btn ds-btn-primary ds-btn-lg"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={16} />
            {saving ? 'Saving Changes...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettings;
